import DOMPurify from 'dompurify';

/**
 * HTML Sanitizer for XSS Protection
 *
 * CKEditor에서 생성된 HTML을 안전하게 렌더링하기 위한 유틸리티
 * DOMPurify를 사용하여 XSS 공격 벡터를 제거합니다.
 */

interface SanitizeOptions {
  /**
   * 허용할 HTML 태그 목록
   * 기본값: CKEditor에서 일반적으로 사용하는 태그들
   */
  ALLOWED_TAGS?: string[];

  /**
   * 허용할 HTML 속성 목록
   * 기본값: 안전한 속성들만 허용
   */
  ALLOWED_ATTR?: string[];

  /**
   * 이미지 URL 변환 여부
   * true일 경우 상대 경로를 절대 경로로 변환
   */
  convertImageUrls?: boolean;
}

/**
 * CKEditor 기본 설정
 * - 텍스트 포맷팅 태그
 * - 목록 태그
 * - 테이블 태그
 * - 미디어 태그
 * - 레이아웃 태그
 */
const DEFAULT_ALLOWED_TAGS = [
  // 텍스트 포맷팅
  'p', 'br', 'strong', 'em', 'u', 's', 'sup', 'sub',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',

  // 목록
  'ul', 'ol', 'li',

  // 테이블
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',

  // 미디어
  'img', 'figure', 'figcaption',

  // 레이아웃
  'div', 'span', 'blockquote', 'pre', 'code',

  // 링크
  'a',
];

/**
 * 안전한 HTML 속성 목록
 * - 스타일링 속성 (제한적)
 * - 접근성 속성
 * - 미디어 속성
 */
const DEFAULT_ALLOWED_ATTR = [
  // 스타일링
  'class', 'style',

  // 접근성
  'alt', 'title', 'aria-label', 'aria-describedby',

  // 링크
  'href', 'target', 'rel',

  // 미디어
  'src', 'width', 'height',

  // 테이블
  'colspan', 'rowspan',

  // 기타
  'id', 'data-*',
];

/**
 * CKEditor 이미지 경로를 절대 URL로 변환
 *
 * @param html - 변환할 HTML 문자열
 * @returns 절대 경로로 변환된 HTML 문자열
 */
const convertImageUrls = (html: string): string => {
  // 프로덕션: REACT_APP_API_URL='' → 상대 경로 유지
  const apiUrl = process.env.REACT_APP_API_URL ?? '';
  return html.replace(/src="\/media\//g, `src="${apiUrl}/media/`);
};

/**
 * HTML을 안전하게 정제 (Sanitize)
 *
 * XSS 공격 벡터를 제거하고 안전한 HTML만 반환합니다.
 *
 * @param dirtyHtml - 정제할 HTML 문자열
 * @param options - 정제 옵션
 * @returns 정제된 안전한 HTML 문자열
 *
 * @example
 * ```tsx
 * import { sanitizeHtml } from './utils/sanitizeHtml';
 *
 * // 기본 사용
 * const cleanHtml = sanitizeHtml(property.description);
 *
 * // 이미지 URL 변환 포함
 * const cleanHtml = sanitizeHtml(property.description, { convertImageUrls: true });
 *
 * // 렌더링
 * <div dangerouslySetInnerHTML={{ __html: cleanHtml }} />
 * ```
 */
export const sanitizeHtml = (
  dirtyHtml: string | null | undefined,
  options: SanitizeOptions = {}
): string => {
  // null/undefined 처리
  if (!dirtyHtml) {
    return '';
  }

  // 이미지 URL 변환 (선택적)
  let html = dirtyHtml;
  if (options.convertImageUrls) {
    html = convertImageUrls(html);
  }

  // DOMPurify 설정
  const config: DOMPurify.Config = {
    ALLOWED_TAGS: options.ALLOWED_TAGS || DEFAULT_ALLOWED_TAGS,
    ALLOWED_ATTR: options.ALLOWED_ATTR || DEFAULT_ALLOWED_ATTR,

    // 위험한 프로토콜 차단
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,

    // 안전하지 않은 속성 제거
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],

    // 안전하지 않은 태그 제거
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'link', 'style'],

    // 빈 태그 유지 (이미지 등)
    KEEP_CONTENT: true,

    // HTML5 파싱
    PARSER_MEDIA_TYPE: 'text/html',
  };

  // HTML 정제
  const clean = DOMPurify.sanitize(html, config);

  return clean;
};

/**
 * React 컴포넌트에서 안전하게 HTML 렌더링
 *
 * @param html - 렌더링할 HTML 문자열
 * @param options - 정제 옵션
 * @returns dangerouslySetInnerHTML 속성 객체
 *
 * @example
 * ```tsx
 * import { createSafeMarkup } from './utils/sanitizeHtml';
 *
 * <div {...createSafeMarkup(property.description, { convertImageUrls: true })} />
 * ```
 */
export const createSafeMarkup = (
  html: string | null | undefined,
  options: SanitizeOptions = {}
): { dangerouslySetInnerHTML: { __html: string } } => {
  return {
    dangerouslySetInnerHTML: {
      __html: sanitizeHtml(html, options),
    },
  };
};

/**
 * 텍스트만 추출 (HTML 태그 모두 제거)
 *
 * @param html - HTML 문자열
 * @returns 순수 텍스트
 *
 * @example
 * ```tsx
 * const plainText = stripHtml('<p>Hello <strong>World</strong></p>');
 * // "Hello World"
 * ```
 */
export const stripHtml = (html: string | null | undefined): string => {
  if (!html) {
    return '';
  }

  // 모든 HTML 태그 제거
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [],
    KEEP_CONTENT: true,
  });

  return clean.trim();
};

/**
 * 상세 설명에서 문의 정보 제거
 *
 * 【 문의 및 상담 】, 전화: 1533-8124 등 문의 관련 텍스트를 제거합니다.
 *
 * @param text - 원본 텍스트
 * @returns 문의 정보가 제거된 텍스트
 */
export const removeContactInfo = (text: string | null | undefined): string => {
  if (!text) {
    return '';
  }

  // 문의 정보 패턴들 제거
  let cleaned = text
    // 【 문의 및 상담 】 전화: 1533-8124 패턴
    .replace(/【\s*문의\s*(및|&)?\s*상담\s*】[^【]*?(전화|연락처)?:?\s*[\d\-]+/gi, '')
    // 전화: 1533-8124 패턴
    .replace(/전화\s*:\s*[\d\-]+/gi, '')
    // 연락처: 1533-8124 패턴
    .replace(/연락처\s*:\s*[\d\-]+/gi, '')
    // 문의전화: 1533-8124 패턴
    .replace(/문의\s*(전화|번호)?\s*:\s*[\d\-]+/gi, '')
    // 상담문의: 1533-8124 패턴
    .replace(/상담\s*(문의)?\s*:\s*[\d\-]+/gi, '')
    // 【 문의 】 1533-8124 패턴
    .replace(/【\s*문의\s*】\s*[\d\-]+/gi, '')
    // 빈 괄호/대괄호 제거
    .replace(/【\s*】/g, '')
    // 연속된 공백/줄바꿈 정리
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s{3,}/g, ' ')
    .trim();

  return cleaned;
};

export default sanitizeHtml;
